import { useEffect, useState } from "react";
import { Outlet } from "react-router";

import { CommandPalette } from "@/app/shell/CommandPalette";
import { DesktopHeader } from "@/app/shell/DesktopHeader";
import { MobileMenu, Sidebar } from "@/app/shell/Sidebar";
import { useSidebarCollapsed } from "@/app/shell/useSidebarCollapsed";
import { MobileTopbar } from "@/app/shell/Topbar";
import { NewProjectDialog } from "@/features/projects/components/NewProjectDialog";
import { NewTaskDialog } from "@/features/tasks/components/NewTaskDialog";
import { useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";

export function AppShell() {
  const t = useT();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, toggleSidebar] = useSidebarCollapsed();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const actions = {
    onSearch: () => {
      setPaletteOpen(true);
    },
    onNewProject: () => {
      setCreatingProject(true);
    },
  };
  const onNewTask = () => {
    setCreatingTask(true);
  };

  return (
    // On desktop only the page area scrolls; the frame (sidebar and header) never moves.
    <div className="flex min-h-dvh bg-canvas lg:h-dvh lg:overflow-hidden">
      <a
        href="#main"
        className="sr-only z-50 rounded-lg bg-ink px-3 py-2 text-sm text-ink-inverse focus:not-sr-only focus:fixed focus:start-3 focus:top-3"
      >
        {t("shell.skipToContent")}
      </a>
      <Sidebar {...actions} collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      <div className="flex min-w-0 flex-1 flex-col lg:h-dvh lg:py-2 lg:pe-2">
        {/* A folded sidebar keeps its full brand row, so the header gives up the same width the
            rail lost; both move together and the header's start edge never shifts. */}
        <div
          className={cn("transition-[margin] duration-200", sidebarCollapsed && "lg:ms-[11.25rem]")}
        >
          <DesktopHeader
            onSearch={actions.onSearch}
            onNewProject={actions.onNewProject}
            onNewTask={onNewTask}
          />
        </div>
        <MobileTopbar
          onOpenPalette={actions.onSearch}
          onOpenMenu={() => {
            setMenuOpen(true);
          }}
        />
        <main
          id="main"
          className="flex min-h-0 flex-1 flex-col bg-surface lg:overflow-y-auto lg:overscroll-contain lg:rounded-xl lg:border lg:border-line lg:shadow-xs"
        >
          <Outlet />
        </main>
      </div>
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onNewProject={actions.onNewProject}
        onNewTask={onNewTask}
      />
      <NewProjectDialog open={creatingProject} onOpenChange={setCreatingProject} />
      <NewTaskDialog open={creatingTask} onOpenChange={setCreatingTask} />
      <MobileMenu open={menuOpen} onOpenChange={setMenuOpen} {...actions} />
    </div>
  );
}
